import type { ScanCaptureDetails, ScanParallelismProbeSnapshot } from "../types/scanWorkflow";

export const PRODUCTION_EXECUTION_PROFILE = {
  schemaVersion: 1,
  contractVersion: "production-browser-execution-profile-v1",
  profileId: "serial-production-v1",
  recognitionMode: "serial",
  productionParallelRecognitionEnabled: false,
  detectionWorkerBudget: 1,
  ocrWorkerBudget: 1,
  branchParallelismEnabled: false,
  probeOnly: true,
} as const;

const PROBE_CONTRACT = "browser-parallelism-probe-v1" as const;
const PROBE_ITERATIONS = 6_000_000;
const IOS_WEBKIT_PROBE_ITERATIONS = 4_000_000;
const PROBE_WARMUP_ITERATIONS = 160_000;
const PROBE_RUN_TIMEOUT_MS = 4_000;

let probePromise: Promise<ScanParallelismProbeSnapshot> | null = null;
let activeProbeWorkers: ProbeWorker[] = [];
let probeSuppressionReason: string | null = null;
let latestProbe: ScanParallelismProbeSnapshot = createBaseSnapshot("not_run");

function finiteNumber(value: number): number | null {
  return Number.isFinite(value) ? Math.round(value * 10) / 10 : null;
}

function isIosWebKitProbeRuntime(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const ios = /iPhone|iPad|iPod/i.test(ua)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const webKit = /AppleWebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return ios && webKit;
}

function createBaseSnapshot(status: ScanParallelismProbeSnapshot["status"]): ScanParallelismProbeSnapshot {
  const logicalCpuCount = typeof navigator !== "undefined" && Number.isFinite(navigator.hardwareConcurrency) && navigator.hardwareConcurrency > 0
    ? navigator.hardwareConcurrency
    : null;
  const workerApiAvailable = typeof Worker !== "undefined";
  const sharedArrayBufferAvailable = typeof SharedArrayBuffer !== "undefined";
  const atomicsAvailable = typeof Atomics !== "undefined";
  const crossOriginIsolated = globalThis.crossOriginIsolated === true;
  return {
    schemaVersion: 1,
    contractVersion: PROBE_CONTRACT,
    status,
    measuredAtUtc: null,
    logicalCpuCount,
    requestedWorkers: 2,
    createdWorkers: 0,
    workerApiAvailable,
    offscreenCanvasAvailable: typeof OffscreenCanvas !== "undefined",
    createImageBitmapAvailable: typeof createImageBitmap === "function",
    crossOriginIsolated,
    sharedArrayBufferAvailable,
    atomicsAvailable,
    wasmAvailable: typeof WebAssembly !== "undefined",
    wasmPthreadsPrerequisitesMet: crossOriginIsolated && sharedArrayBufferAvailable && atomicsAvailable,
    sequentialWallMs: null,
    parallelWallMs: null,
    estimatedSpeedup: null,
    classification: status === "not_run" ? "pending" : "inconclusive",
    checksumMatch: null,
    fallbackReason: null,
    error: null,
  };
}

function cloneSnapshot(snapshot: ScanParallelismProbeSnapshot): ScanParallelismProbeSnapshot {
  return { ...snapshot };
}

export function getRecognitionParallelismProbeSnapshot(): ScanParallelismProbeSnapshot {
  return cloneSnapshot(latestProbe);
}

export function recognitionParallelismEvidence(snapshot = latestProbe): Record<string, unknown> {
  return {
    schema_version: snapshot.schemaVersion,
    contract_version: snapshot.contractVersion,
    status: snapshot.status,
    measured_at_utc: snapshot.measuredAtUtc,
    logical_cpu_count: snapshot.logicalCpuCount,
    requested_workers: snapshot.requestedWorkers,
    created_workers: snapshot.createdWorkers,
    worker_api_available: snapshot.workerApiAvailable,
    offscreen_canvas_available: snapshot.offscreenCanvasAvailable,
    create_image_bitmap_available: snapshot.createImageBitmapAvailable,
    cross_origin_isolated: snapshot.crossOriginIsolated,
    shared_array_buffer_available: snapshot.sharedArrayBufferAvailable,
    atomics_available: snapshot.atomicsAvailable,
    wasm_available: snapshot.wasmAvailable,
    wasm_pthreads_prerequisites_met: snapshot.wasmPthreadsPrerequisitesMet,
    sequential_wall_ms: snapshot.sequentialWallMs,
    parallel_wall_ms: snapshot.parallelWallMs,
    estimated_speedup: snapshot.estimatedSpeedup,
    classification: snapshot.classification,
    checksum_match: snapshot.checksumMatch,
    fallback_reason: snapshot.fallbackReason,
    error: snapshot.error,
  };
}

export function productionExecutionProfileEvidence(details?: ScanCaptureDetails | null): Record<string, unknown> {
  const detectionCandidateEvaluation = details?.detection?.parallelDetectionCandidateEvaluation ?? null;
  const candidateBatch = details?.rectification?.productionParallelCandidateBatch ?? null;
  const nonPlanarRescue = details?.rectification?.productionParallelNonPlanarRescue ?? null;
  const finalOcr = details?.ocr?.parallelProduction ?? null;
  const detectionParallel = detectionCandidateEvaluation?.status === "parallel" && detectionCandidateEvaluation.productionInfluenced === true;
  const candidateBatchParallel = candidateBatch?.status === "parallel" && candidateBatch.productionInfluenced === true;
  const nonPlanarParallel = nonPlanarRescue?.status === "parallel" && nonPlanarRescue.productionInfluenced === true;
  const finalOcrParallel = finalOcr?.status === "parallel" && finalOcr.productionInfluenced === true;
  const anyParallel = detectionParallel || candidateBatchParallel || nonPlanarParallel || finalOcrParallel;
  const pv15PerformanceFreeze = details?.criticalPathOrchestration?.contractVersion === "pv15-full-fidelity-performance-freeze-v1";
  const profileId = detectionParallel && pv15PerformanceFreeze
    ? "pv15-parallel-production-v1"
    : detectionParallel
    ? "pv14-parallel-production-v1"
    : nonPlanarParallel && (candidateBatchParallel || finalOcrParallel)
      ? "pv12-parallel-production-v1"
      : nonPlanarParallel
        ? "pv12-parallel-non-planar-rescue-production-v1"
        : candidateBatchParallel && finalOcrParallel
          ? "pv9-parallel-candidate-plus-final-ocr-production-v1"
          : finalOcrParallel
            ? "pv9-parallel-final-ocr-production-v1"
            : candidateBatchParallel
              ? "pv8-parallel-candidate-batch-production-v1"
              : PRODUCTION_EXECUTION_PROFILE.profileId;
  const scopes = [
    detectionParallel ? "detection-candidate-evaluation" : null,
    candidateBatchParallel ? "top-k-candidate-batch" : null,
    nonPlanarParallel ? "non-planar-rescue" : null,
    finalOcrParallel ? "final-four-field-ocr" : null,
  ].filter((value): value is string => Boolean(value));
  const parallelScope = scopes.length > 0 ? scopes.join("+") : null;
  return {
    schema_version: PRODUCTION_EXECUTION_PROFILE.schemaVersion,
    contract_version: anyParallel
      ? detectionParallel
        ? pv15PerformanceFreeze ? "production-browser-execution-profile-v6" : "production-browser-execution-profile-v5"
        : "production-browser-execution-profile-v4"
      : PRODUCTION_EXECUTION_PROFILE.contractVersion,
    profile_id: profileId,
    recognition_mode: anyParallel ? "hybrid-serial-parallel" : PRODUCTION_EXECUTION_PROFILE.recognitionMode,
    production_parallel_recognition_enabled: anyParallel,
    parallel_scope: parallelScope,
    detection_worker_budget: detectionParallel ? 2 : PRODUCTION_EXECUTION_PROFILE.detectionWorkerBudget,
    candidate_rectification_worker_budget: candidateBatchParallel ? 2 : 1,
    non_planar_rescue_worker_budget: nonPlanarParallel ? 2 : 1,
    rerank_ocr_worker_budget: candidateBatchParallel || nonPlanarParallel ? 2 : 1,
    final_ocr_worker_budget: finalOcrParallel ? 2 : 1,
    ocr_worker_budget: (candidateBatchParallel || nonPlanarParallel || finalOcrParallel) ? 2 : PRODUCTION_EXECUTION_PROFILE.ocrWorkerBudget,
    branch_parallelism_enabled: anyParallel,
    probe_only: anyParallel ? false : PRODUCTION_EXECUTION_PROFILE.probeOnly,
    serial_fallback_profile: "serial-production-v1",
  };
}

type ProbeWorker = {
  worker: Worker;
  run: (iterations: number) => Promise<{ durationMs: number; checksum: number }>;
  close: () => void;
};

function createProbeWorker(blobUrl: string, workerIndex: number): ProbeWorker {
  const worker = new Worker(blobUrl, { name: `recognition-parallelism-probe-${workerIndex}` });
  let sequence = 0;
  const pending = new Map<number, { resolve: (value: { durationMs: number; checksum: number }) => void; reject: (reason: unknown) => void; timeout: number }>();
  worker.onmessage = (event: MessageEvent<{ id?: number; durationMs?: number; checksum?: number }>) => {
    const id = event.data?.id;
    if (typeof id !== "number") return;
    const request = pending.get(id);
    if (!request) return;
    window.clearTimeout(request.timeout);
    pending.delete(id);
    request.resolve({
      durationMs: Number(event.data.durationMs ?? 0),
      checksum: Number(event.data.checksum ?? 0) >>> 0,
    });
  };
  worker.onerror = (event: ErrorEvent) => {
    for (const request of pending.values()) {
      window.clearTimeout(request.timeout);
      request.reject(new Error(event.message || "Parallelitäts-Probe-Worker fehlgeschlagen."));
    }
    pending.clear();
  };
  return {
    worker,
    run(iterations) {
      const id = ++sequence;
      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          pending.delete(id);
          reject(new Error("Parallelitäts-Probe hat das 4-s-Limit pro Workunit überschritten."));
        }, PROBE_RUN_TIMEOUT_MS);
        pending.set(id, { resolve, reject, timeout });
        worker.postMessage({ id, iterations });
      });
    },
    close() {
      for (const request of pending.values()) {
        window.clearTimeout(request.timeout);
        request.reject(new Error("Parallelitäts-Probe beendet."));
      }
      pending.clear();
      worker.terminate();
    },
  };
}


export function suppressRecognitionParallelismProbe(reason = "CAPTURE_RUNTIME_SERIALIZED"): void {
  probeSuppressionReason = reason;
  for (const item of activeProbeWorkers) item.close();
  activeProbeWorkers = [];
  latestProbe = {
    ...createBaseSnapshot("unsupported"),
    measuredAtUtc: new Date().toISOString(),
    classification: "unsupported",
    fallbackReason: reason,
  };
}

export async function warmRecognitionParallelismProbe(): Promise<ScanParallelismProbeSnapshot> {
  if (latestProbe.status === "ready" || latestProbe.status === "unsupported") {
    return cloneSnapshot(latestProbe);
  }
  if (probePromise) return probePromise.then(cloneSnapshot);

  if (probeSuppressionReason) return cloneSnapshot(latestProbe);

  latestProbe = createBaseSnapshot("running");
  const createdProbe = (async () => {
    if (!latestProbe.workerApiAvailable) {
      latestProbe = {
        ...createBaseSnapshot("unsupported"),
        measuredAtUtc: new Date().toISOString(),
        classification: "unsupported",
        fallbackReason: "WORKER_API_UNAVAILABLE",
      };
      return latestProbe;
    }
    if ((latestProbe.logicalCpuCount ?? 0) < 2) {
      latestProbe = {
        ...createBaseSnapshot("unsupported"),
        measuredAtUtc: new Date().toISOString(),
        classification: "unsupported",
        fallbackReason: "LOGICAL_CPU_COUNT_LT_2",
      };
      return latestProbe;
    }

    const source = `self.onmessage=(event)=>{const id=event.data.id;const iterations=Math.max(1,Number(event.data.iterations)||1);const started=performance.now();let x=0x12345678>>>0;for(let i=0;i<iterations;i+=1){x^=(x<<13);x^=(x>>>17);x^=(x<<5);x=Math.imul(x>>>0,2654435761)>>>0;}self.postMessage({id,durationMs:performance.now()-started,checksum:x>>>0});};`;
    const blobUrl = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
    const workers: ProbeWorker[] = [];
    try {
      workers.push(createProbeWorker(blobUrl, 1), createProbeWorker(blobUrl, 2));
      activeProbeWorkers = workers;
      latestProbe = { ...latestProbe, createdWorkers: workers.length };

      // Warm both workers before comparing sequential vs. simultaneous fixed-work runs.
      // WebKit/iOS bekommt eine etwas kleinere bounded CPU-Workunit, damit der
      // Kompatibilitaets-Probe selbst nie zum langen Capture-Blocker wird. Die
      // Entscheidung bleibt identisch: gleiche Workunit seriell vs. parallel,
      // gleiche Checksumme, messbarer Speedup oder fail-closed Serial-Fallback.
      const iterations = isIosWebKitProbeRuntime() ? IOS_WEBKIT_PROBE_ITERATIONS : PROBE_ITERATIONS;
      await Promise.all(workers.map((item) => item.run(PROBE_WARMUP_ITERATIONS)));

      const sequentialStarted = performance.now();
      const sequentialA = await workers[0].run(iterations);
      const sequentialB = await workers[1].run(iterations);
      const sequentialWallMs = performance.now() - sequentialStarted;

      const parallelStarted = performance.now();
      const [parallelA, parallelB] = await Promise.all([
        workers[0].run(iterations),
        workers[1].run(iterations),
      ]);
      const parallelWallMs = performance.now() - parallelStarted;
      const estimatedSpeedup = parallelWallMs > 0 ? sequentialWallMs / parallelWallMs : null;
      const checksumMatch = sequentialA.checksum === sequentialB.checksum
        && sequentialA.checksum === parallelA.checksum
        && sequentialA.checksum === parallelB.checksum;
      const classification: ScanParallelismProbeSnapshot["classification"] = !checksumMatch
        ? "inconclusive"
        : estimatedSpeedup !== null && estimatedSpeedup >= 1.35
          ? "likely_parallel"
          : estimatedSpeedup !== null && estimatedSpeedup <= 1.15
            ? "likely_serialized"
            : "inconclusive";

      latestProbe = {
        ...createBaseSnapshot("ready"),
        measuredAtUtc: new Date().toISOString(),
        createdWorkers: workers.length,
        sequentialWallMs: finiteNumber(sequentialWallMs),
        parallelWallMs: finiteNumber(parallelWallMs),
        estimatedSpeedup: estimatedSpeedup === null ? null : Math.round(estimatedSpeedup * 1000) / 1000,
        classification,
        checksumMatch,
        fallbackReason: classification === "likely_parallel" ? null : "PROBE_DID_NOT_PROVE_PARALLEL_SPEEDUP",
      };
      return latestProbe;
    } catch (error: unknown) {
      if (probeSuppressionReason) return latestProbe;
      latestProbe = {
        ...createBaseSnapshot("error"),
        measuredAtUtc: new Date().toISOString(),
        createdWorkers: workers.length,
        classification: "error",
        fallbackReason: "PROBE_RUNTIME_ERROR",
        error: error instanceof Error ? error.message : String(error),
      };
      return latestProbe;
    } finally {
      workers.forEach((item) => item.close());
      if (activeProbeWorkers === workers) activeProbeWorkers = [];
      URL.revokeObjectURL(blobUrl);
    }
  })().finally(() => {
    probePromise = null;
  });
  probePromise = createdProbe;

  return createdProbe.then(cloneSnapshot);
}
